/**
 * Practitioner Session API Client
 *
 * Dedicated API client for practitioner-client session generation.
 * Simplified, focused, and type-safe.
 */

// ============================================================================
// TYPES
// ============================================================================

export interface PractitionerSessionCreateRequest {
  // Metadata
  title: string;
  session_notes?: string;

  // Client/Group (mutually exclusive)
  client_id?: string;
  group_session_id?: string;

  // Category & length
  category_id: string;
  session_length: string;

  // Session configuration
  environment: string;
  meditation_style: string;
  seeking_goal: string;
  desired_feeling: string;
  voice_style: string;

  // Voice & audio
  voice_gender: string;
  specific_voice_id?: string;  // Standardized field name
  background_music_file?: string;
  include_background_sounds: boolean;
  background_music_volume_db?: number;

  // Personalization
  use_personal_biography?: boolean;
  client_biography?: string;
  content_exclusions?: string[];
  content_requirements?: string[];
  session_directives?: string;
  client_first_name?: string;
  client_last_name?: string;
  practitioner_name?: string;

  // Questionnaire
  questionnaire_responses: Record<string, any>;
  custom_prompt?: string;

  // Custom text mode fields
  custom_text_mode?: 'verbatim' | 'blend_beginning' | 'blend_end' | 'blend_middle' | 'blend_woven' | 'blend_custom';
  custom_text?: string;
  custom_text_instructions?: string;

  // Audio mixing
  save_mixed_version?: boolean;
}

export interface PractitionerSessionCreateResponse {
  session_id: string;
  job_id: string;
  status: string;
}

export interface SessionStatus {
  session_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress: number;
  audio_url?: string;
  mixed_audio_url?: string;
  generated_content?: string;
  error_message?: string;
  job_id?: string;
}

export interface JobStatus {
  id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  priority: string;
  job_type: string;
  progress: number;
  queue_position?: number | null;
  estimated_completion?: string | null;
  created_at: string;
  completed_at?: string | null;
  error_message?: string | null;
  result_data?: {
    session_id: string;
    audio_url: string;
    mixed_audio_url?: string | null;
    background_audio_url?: string | null;
    script_text: string;
    duration: number;
    mode?: string;
    word_count?: number;
  };
}

// ============================================================================
// API CLIENT
// ============================================================================

class PractitionerSessionAPI {
  private baseUrl: string;
  private getToken?: () => Promise<string | null>;

  constructor(getToken?: () => Promise<string | null>) {
    this.baseUrl = import.meta.env.VITE_BACKEND_URL || window.location.origin;
    this.getToken = getToken;
  }

  /**
   * Get authentication headers
   */
  private async getHeaders(): Promise<HeadersInit> {
    const token = this.getToken ? await this.getToken() : null;

    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    };
  }

  /**
   * Create a new practitioner session
   */
  async createSession(
    request: PractitionerSessionCreateRequest
  ): Promise<PractitionerSessionCreateResponse> {
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/practitioner/sessions`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(error.detail || `Failed to create session: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Poll job status
   */
  async pollJobStatus(jobId: string): Promise<JobStatus> {
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/jobs/job/${jobId}`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(error.detail || `Failed to get job status: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Poll session status (alternative to job status)
   */
  async pollSessionStatus(sessionId: string): Promise<SessionStatus> {
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/practitioner/sessions/${sessionId}/status`, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(error.detail || `Failed to get session status: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Wait for job completion with polling
   */
  async waitForCompletion(
    jobId: string,
    onProgress?: (progress: number) => void
  ): Promise<JobStatus> {
    const pollInterval = 1000; // 1 second
    const maxAttempts = 600; // 10 minutes max
    let attempts = 0;

    while (attempts < maxAttempts) {
      const status = await this.pollJobStatus(jobId);

      // Update progress callback (convert 0.0-1.0 to 0-100 percentage)
      if (onProgress) {
        const progressPercentage = Math.round(status.progress * 100);
        onProgress(progressPercentage);
      }

      // Check if complete
      if (status.status === 'completed') {
        return status;
      }

      if (status.status === 'failed') {
        throw new Error(status.error_message || 'Job failed');
      }

      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, pollInterval));
      attempts++;
    }

    throw new Error('Job timed out after 10 minutes');
  }

  /**
   * Create session and wait for completion (convenience method)
   */
  async createAndWaitForSession(
    request: PractitionerSessionCreateRequest,
    onProgress?: (progress: number) => void
  ): Promise<{
    sessionId: string;
    audioUrl: string;
    mixedAudioUrl?: string;
    generatedContent: string;
  }> {
    // Create session
    const createResponse = await this.createSession(request);

    // Wait for completion
    const jobStatus = await this.waitForCompletion(createResponse.job_id, onProgress);

    if (!jobStatus.result_data) {
      throw new Error('Job completed but no result returned');
    }

    return {
      sessionId: jobStatus.result_data.session_id,
      audioUrl: jobStatus.result_data.audio_url,
      mixedAudioUrl: jobStatus.result_data.mixed_audio_url || undefined,
      generatedContent: jobStatus.result_data.script_text,
    };
  }

  /**
   * Create share link for a session
   */
  async createShareLink(
    sessionId: string,
    options: {
      save_mixed_version?: boolean;
      delivery_method?: string;
      send_email?: boolean;
      recipient_email?: string;
    } = {}
  ): Promise<{
    share_url: string;
    access_token: string;
    mixed_audio_url?: string;
    email_sent: boolean;
  }> {
    const headers = await this.getHeaders();

    const response = await fetch(`${this.baseUrl}/practitioner/sessions/${sessionId}/share`, {
      method: 'POST',
      headers,
      body: JSON.stringify(options),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Unknown error' }));
      throw new Error(error.detail || `Failed to create share link: ${response.statusText}`);
    }

    return response.json();
  }
}

// ============================================================================
// FACTORY FUNCTION
// ============================================================================

/**
 * Create an API client instance with a token provider
 *
 * Example usage:
 * ```
 * const api = createPractitionerSessionAPI(async () => {
 *   const { data: { session } } = await backendClient.auth.getSession();
 *   return session?.access_token || null;
 * });
 * ```
 */
export function createPractitionerSessionAPI(
  getToken?: () => Promise<string | null>
): PractitionerSessionAPI {
  return new PractitionerSessionAPI(getToken);
}

// ============================================================================
// EXPORT API CLIENT CLASS (for direct instantiation)
// ============================================================================

export { PractitionerSessionAPI };
